/**
 * 时间处理工具函数
 * 处理时区转换、时间戳格式化等功能
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// 扩展dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

// 系统默认时区 (可配置)
const DEFAULT_TIMEZONE = 'Asia/Shanghai';

/**
 * 获取当前时间戳（毫秒）
 * @returns 当前时间的UTC时间戳
 */
export const getCurrentTimestamp = (): number => {
  return Date.now();
};

/**
 * 将日期字符串转换为时间戳（毫秒）
 * @param dateString 日期字符串，格式如 '2025-08-21'
 * @param timezone 时区，默认为系统时区
 * @returns UTC时间戳（毫秒）
 */
export const dateStringToTimestamp = (
  dateString: string, 
  timezone: string = DEFAULT_TIMEZONE
): number => {
  if (!dateString) {
    return 0;
  }
  
  try {
    // 解析日期字符串并转换为指定时区的时间戳
    const date = dayjs.tz(dateString, timezone);
    if (!date.isValid()) {
      console.warn(`Invalid date string: ${dateString}`);
      return 0;
    }
    return date.valueOf(); // 返回UTC时间戳
  } catch (error) {
    console.error('Error converting date string to timestamp:', error);
    return 0;
  }
};

/**
 * 将时间戳转换为日期字符串
 * @param timestamp 时间戳（毫秒）
 * @param format 格式化字符串，默认为 'YYYY-MM-DD'
 * @param timezone 时区，默认为系统时区
 * @returns 格式化后的日期字符串
 */
export const timestampToDateString = (
  timestamp: number, 
  format: string = 'YYYY-MM-DD',
  timezone: string = DEFAULT_TIMEZONE
): string => {
  if (!timestamp || timestamp <= 0) {
    return '';
  }
  
  try {
    return dayjs(timestamp).tz(timezone).format(format);
  } catch (error) {
    console.error('Error converting timestamp to date string:', error);
    return '';
  }
};

/**
 * 将时间戳转换为日期时间字符串
 * @param timestamp 时间戳（毫秒）
 * @param format 格式化字符串，默认为 'YYYY-MM-DD HH:mm:ss'
 * @param timezone 时区，默认为系统时区
 * @returns 格式化后的日期时间字符串
 */
export const timestampToDateTime = (
  timestamp: number, 
  format: string = 'YYYY-MM-DD HH:mm:ss',
  timezone: string = DEFAULT_TIMEZONE
): string => {
  if (!timestamp || timestamp <= 0) {
    return '-';
  }
  
  try {
    return dayjs(timestamp).tz(timezone).format(format);
  } catch (error) {
    console.error('Error converting timestamp to datetime:', error);
    return '-';
  }
};

/**
 * 获取指定日期的开始时间（00:00:00）时间戳
 * @param dateString 日期字符串，格式如 '2025-08-21'
 * @param timezone 时区，默认为系统时区
 * @returns 该日期开始时间的UTC时间戳
 */
export const getDateStartTimestamp = (
  dateString: string,
  timezone: string = DEFAULT_TIMEZONE
): number => {
  if (!dateString) {
    return 0;
  }
  
  try {
    const date = dayjs.tz(dateString, timezone).startOf('day');
    return date.valueOf();
  } catch (error) {
    console.error('Error getting date start timestamp:', error);
    return 0;
  }
};

/**
 * 获取指定日期的结束时间（23:59:59）时间戳
 * @param dateString 日期字符串，格式如 '2025-08-21'
 * @param timezone 时区，默认为系统时区
 * @returns 该日期结束时间的UTC时间戳
 */
export const getDateEndTimestamp = (
  dateString: string,
  timezone: string = DEFAULT_TIMEZONE
): number => {
  if (!dateString) {
    return 0;
  }
  
  try {
    const date = dayjs.tz(dateString, timezone).endOf('day');
    return date.valueOf();
  } catch (error) {
    console.error('Error getting date end timestamp:', error);
    return 0;
  }
};

/**
 * 验证日期字符串是否有效
 * @param dateString 日期字符串
 * @returns 是否为有效日期
 */
export const isValidDateString = (dateString: string): boolean => {
  if (!dateString) {
    return false;
  }
  
  try {
    const date = dayjs(dateString);
    return date.isValid();
  } catch (error) {
    return false;
  }
};

/**
 * 比较两个日期时间戳
 * @param timestamp1 第一个时间戳
 * @param timestamp2 第二个时间戳
 * @returns 0: 相等, 1: timestamp1 > timestamp2, -1: timestamp1 < timestamp2
 */
export const compareTimestamps = (timestamp1: number, timestamp2: number): number => {
  if (timestamp1 === timestamp2) return 0;
  return timestamp1 > timestamp2 ? 1 : -1;
};

/**
 * 获取今天的日期字符串
 * @param timezone 时区，默认为系统时区
 * @returns 今天的日期字符串 'YYYY-MM-DD'
 */
export const getTodayDateString = (timezone: string = DEFAULT_TIMEZONE): string => {
  return dayjs().tz(timezone).format('YYYY-MM-DD');
};

/**
 * 获取明天的日期字符串
 * @param timezone 时区，默认为系统时区
 * @returns 明天的日期字符串 'YYYY-MM-DD'
 */
export const getTomorrowDateString = (timezone: string = DEFAULT_TIMEZONE): string => {
  return dayjs().tz(timezone).add(1, 'day').format('YYYY-MM-DD');
};

/**
 * 为执行周期配置生成合适的首次执行时间戳
 * @param dateString 日期字符串，格式如 '2025-08-21'
 * @param timezone 时区，默认为系统时区
 * @returns 首次执行时间的UTC时间戳（设置为当天的08:00:00）
 */
export const generateFirstExecutionTimestamp = (
  dateString: string,
  timezone: string = DEFAULT_TIMEZONE
): number => {
  if (!dateString) {
    return 0;
  }
  
  try {
    // 设置为指定日期的上午9点执行
    const executionTime = dayjs.tz(dateString, timezone)
      .hour(16)
      .minute(0)
      .second(0)
      .millisecond(0);
    
    return executionTime.valueOf();
  } catch (error) {
    console.error('Error generating first execution timestamp:', error);
    return 0;
  }
};

/**
 * 根据内存规范生成时间戳
 * 符合"时间字段命名与生成规范"中的要求
 * @param offsetHours 相对当前时间的小时偏移量，负数表示过去时间
 * @returns 生成的时间戳
 */
export const generateMockTimestamp = (offsetHours: number = 0): number => {
  return Date.now() - (offsetHours * 60 * 60 * 1000);
};

// 导出常用的时区常量
export const TIMEZONES = {
  SHANGHAI: 'Asia/Shanghai',
  BEIJING: 'Asia/Shanghai', // 北京时间等同于上海时间
  UTC: 'UTC',
  NEW_YORK: 'America/New_York',
  LONDON: 'Europe/London',
  TOKYO: 'Asia/Tokyo',
} as const;

// 导出常用的日期格式
export const DATE_FORMATS = {
  DATE_ONLY: 'YYYY-MM-DD',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  TIME_ONLY: 'HH:mm:ss',
  CHINESE_DATE: 'YYYY/MM/DD',
  CHINESE_DATETIME: 'YYYY/MM/DD HH:mm:ss',
} as const;